/**
 * One server-sent event stream per browser, shared by every tab that asks for the same stream.
 *
 * Over plain HTTP/1.1 (local dev) a browser opens at most six connections to one server. A stream
 * per tab used all six once six tabs were open, and every other request waited behind them. Here
 * the tabs elect one leader with the Web Locks API: only the leader opens the EventSource and it
 * passes each event to the other tabs over a BroadcastChannel. When the leader tab closes, its
 * lock is released and the next tab takes over the stream.
 */

export interface SharedEventStreamOptions {
  /** Tabs with the same key share one stream (include the server, tenant and user). */
  key: string;
  /** Called on every (re)connect so a fresh token is used. Null when the user is not signed in. */
  buildUrl: () => string | null;
  /** Named server events to pass on. */
  eventNames: readonly string[];
  /** Named server event that confirms the stream is live. */
  connectedEventName?: string;
  onEvent: (eventName: string, data: string) => void;
  onConnectedChange: (connected: boolean) => void;
  /** Wait before reconnecting after an error. */
  reconnectDelayMs?: number;
}

type ChannelMessage =
  | { kind: 'event'; eventName: string; data: string }
  | { kind: 'status'; connected: boolean }
  | { kind: 'status-request' };

interface RunningStream {
  stop: () => void;
  isConnected: () => boolean;
}

/** Opens the EventSource in this tab and reconnects after errors until stopped. */
function runStream(options: SharedEventStreamOptions): RunningStream {
  const {
    buildUrl,
    eventNames,
    connectedEventName = 'connected',
    onEvent,
    onConnectedChange,
    reconnectDelayMs = 5000,
  } = options;
  let source: EventSource | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let connected = false;
  let stopped = false;

  const setConnected = (value: boolean) => {
    if (connected === value) return;
    connected = value;
    onConnectedChange(value);
  };

  const connect = () => {
    if (stopped) return;
    const url = buildUrl();
    if (!url) {
      retryTimer = setTimeout(connect, reconnectDelayMs);
      return;
    }
    const current = new EventSource(url);
    source = current;
    current.addEventListener(connectedEventName, () => setConnected(true));
    for (const eventName of eventNames) {
      current.addEventListener(eventName, (e: MessageEvent<string>) => onEvent(eventName, e.data));
    }
    current.onerror = () => {
      setConnected(false);
      current.close();
      if (source === current) source = null;
      if (!stopped) retryTimer = setTimeout(connect, reconnectDelayMs);
    };
  };

  connect();

  return {
    stop: () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      source?.close();
      source = null;
      setConnected(false);
    },
    isConnected: () => connected,
  };
}

/**
 * Subscribes this tab to the shared stream. Returns a function that unsubscribes; if this tab
 * was the leader, another open tab takes the stream over.
 */
export function openSharedEventStream(options: SharedEventStreamOptions): () => void {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof BroadcastChannel === 'undefined') {
    const stream = runStream(options);
    return stream.stop;
  }

  const name = `shared-sse:${options.key}`;
  const channel = new BroadcastChannel(name);
  const lockRequest = new AbortController();
  let leader: RunningStream | null = null;
  let releaseLock: (() => void) | null = null;
  let closed = false;

  const post = (message: ChannelMessage) => channel.postMessage(message);

  channel.onmessage = (message: MessageEvent<ChannelMessage>) => {
    const data = message.data;
    if (data.kind === 'event') options.onEvent(data.eventName, data.data);
    else if (data.kind === 'status') options.onConnectedChange(data.connected);
    else if (data.kind === 'status-request' && leader) {
      post({ kind: 'status', connected: leader.isConnected() });
    }
  };
  // A tab that joins while another tab leads asks for the current status.
  post({ kind: 'status-request' });

  locks
    .request(name, { signal: lockRequest.signal }, () => {
      if (closed) return undefined;
      leader = runStream({
        ...options,
        onEvent: (eventName, data) => {
          options.onEvent(eventName, data);
          post({ kind: 'event', eventName, data });
        },
        onConnectedChange: (connected) => {
          options.onConnectedChange(connected);
          post({ kind: 'status', connected });
        },
      });
      // Hold the lock until this tab unsubscribes or closes.
      return new Promise<void>((resolve) => {
        releaseLock = resolve;
      });
    })
    .catch(() => {
      // Aborted: this tab unsubscribed before it became the leader.
    });

  return () => {
    closed = true;
    lockRequest.abort();
    leader?.stop();
    leader = null;
    releaseLock?.();
    releaseLock = null;
    channel.close();
  };
}
